import React, { useState } from 'react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useAuth } from '../lib/auth';

type View = 'login' | 'register' | 'confirm' | 'join' | 'forgot' | 'reset';

export function WelcomePage() {
  const { status, email, register, confirmRegistration, login, joinWithCode } = useAuth();
  const [view, setView] = useState<View>(status === 'confirm_signup' ? 'confirm' : 'login');
  const [formEmail, setFormEmail] = useState(email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [transferCode, setTransferCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  React.useEffect(() => {
    if (status === 'confirm_signup') setView('confirm');
  }, [status]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const result = await login(formEmail, password);
    if (!result.ok) setError(result.error || 'Login failed');
    setBusy(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setBusy(true);
    const result = await register(formEmail, password);
    if (!result.ok) setError(result.error || 'Registration failed');
    else setView('confirm');
    setBusy(false);
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const result = await confirmRegistration(formEmail, code);
    if (result.ok) {
      setMessage('Email verified! Please sign in.');
      setView('login');
      setPassword('');
    } else {
      setError(result.error || 'Verification failed');
    }
    setBusy(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmail) { setError('Please enter your email'); return; }
    setError('');
    setBusy(true);
    try {
      await resetPassword({ username: formEmail });
      setMessage('Reset code sent to your email.');
      setView('reset');
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset code');
    }
    setBusy(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError('');
    setBusy(true);
    try {
      await confirmResetPassword({ username: formEmail, confirmationCode: code, newPassword });
      setMessage('Password reset! Please sign in with your new password.');
      setView('login');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password');
    }
    setBusy(false);
  };

  const handleJoin = async () => {
    if (transferCode.length < 6) return;
    setError('');
    setBusy(true);
    const ok = await joinWithCode(transferCode);
    if (!ok) setError('Invalid or expired code.');
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sand-50 px-4">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-adventure-light via-adventure to-adventure-dark" />

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-adventure/10 flex items-center justify-center mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                 strokeLinecap="round" strokeLinejoin="round" className="text-adventure">
              <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
              <path d="M9 3v14"/><path d="M15 7v14"/>
            </svg>
          </div>
          <h1 className="font-display text-3xl text-sand-900">Adventure Editor</h1>
          <p className="text-sand-500 mt-1">Create and share your travel stories</p>
        </div>

        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-700">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Login */}
        {view === 'login' && (
          <form onSubmit={handleLogin} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">Sign In</h2>

            <input className="input" type="email" placeholder="Email" value={formEmail}
              onChange={e => setFormEmail(e.target.value)} autoFocus required />

            <input className="input" type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} required />

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3">
              {busy ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="text-center space-y-2 pt-2">
              <button type="button" onClick={() => { setView('forgot'); setError(''); setMessage(''); }}
                className="text-sand-500 text-sm hover:underline">
                Forgot password?
              </button>
              <div className="border-t border-sand-200 pt-3 mt-3 space-y-2">
                <button type="button" onClick={() => { setView('register'); setError(''); setMessage(''); }}
                  className="text-adventure text-sm hover:underline">
                  Create an account
                </button>
                <span className="text-sand-300 block">or</span>
                <button type="button" onClick={() => { setView('join'); setError(''); }}
                  className="text-sand-500 text-sm hover:underline">
                  Join with transfer code
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Register */}
        {view === 'register' && (
          <form onSubmit={handleRegister} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">Create Account</h2>

            <input className="input" type="email" placeholder="Email" value={formEmail}
              onChange={e => setFormEmail(e.target.value)} autoFocus required />

            <input className="input" type="password" placeholder="Password (min 8 characters)" value={password}
              onChange={e => setPassword(e.target.value)} required minLength={8} />

            <input className="input" type="password" placeholder="Confirm password" value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} required />

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3">
              {busy ? 'Creating account...' : 'Create Account'}
            </button>

            <p className="text-center">
              <button type="button" onClick={() => { setView('login'); setError(''); }}
                className="text-adventure text-sm hover:underline">
                Already have an account? Sign in
              </button>
            </p>
          </form>
        )}

        {/* Confirm email */}
        {view === 'confirm' && (
          <form onSubmit={handleConfirm} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">Verify Email</h2>
            <p className="text-sand-500 text-sm">
              We sent a verification code to <strong>{formEmail}</strong>
            </p>

            <input className="input text-center text-2xl tracking-[0.3em] font-mono" placeholder="123456"
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus required maxLength={6} />

            <button type="submit" disabled={busy || code.length < 6} className="btn-primary w-full justify-center py-3">
              {busy ? 'Verifying...' : 'Verify Email'}
            </button>

            <p className="text-center">
              <button type="button" onClick={() => { setView('login'); setError(''); }}
                className="text-sand-500 text-sm hover:underline">
                Back to sign in
              </button>
            </p>
          </form>
        )}

        {/* Forgot password — request code */}
        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">Reset Password</h2>
            <p className="text-sand-500 text-sm">
              Enter your email and we'll send you a reset code.
            </p>

            <input className="input" type="email" placeholder="Email" value={formEmail}
              onChange={e => setFormEmail(e.target.value)} autoFocus required />

            <button type="submit" disabled={busy || !formEmail} className="btn-primary w-full justify-center py-3">
              {busy ? 'Sending...' : 'Send Reset Code'}
            </button>

            <p className="text-center">
              <button type="button" onClick={() => { setView('login'); setError(''); setMessage(''); }}
                className="text-sand-500 text-sm hover:underline">
                Back to sign in
              </button>
            </p>
          </form>
        )}

        {/* Reset password — enter code + new password */}
        {view === 'reset' && (
          <form onSubmit={handleResetPassword} className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">New Password</h2>
            <p className="text-sand-500 text-sm">
              Enter the code sent to <strong>{formEmail}</strong> and your new password.
            </p>

            <input className="input text-center text-2xl tracking-[0.3em] font-mono" placeholder="123456"
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus required maxLength={6} />

            <input className="input" type="password" placeholder="New password (min 8 characters)"
              value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} />

            <button type="submit" disabled={busy || code.length < 6 || newPassword.length < 8}
              className="btn-primary w-full justify-center py-3">
              {busy ? 'Resetting...' : 'Reset Password'}
            </button>

            <p className="text-center">
              <button type="button" onClick={() => { setView('login'); setError(''); setMessage(''); }}
                className="text-sand-500 text-sm hover:underline">
                Back to sign in
              </button>
            </p>
          </form>
        )}

        {/* Join with transfer code */}
        {view === 'join' && (
          <div className="card p-6 space-y-4">
            <h2 className="font-display text-xl text-sand-900">Join Shared Adventures</h2>
            <p className="text-sand-500 text-sm">
              Enter the 6-character code from your family member
            </p>

            <input className="input text-center text-2xl tracking-[0.3em] font-mono uppercase" placeholder="ABC123"
              value={transferCode} maxLength={6}
              onChange={e => setTransferCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} autoFocus />

            <button onClick={handleJoin} disabled={transferCode.length < 6 || busy}
              className="btn-primary w-full justify-center py-3">
              {busy ? 'Joining...' : 'Join Adventures'}
            </button>

            <p className="text-center">
              <button type="button" onClick={() => { setView('login'); setError(''); }}
                className="text-sand-500 text-sm hover:underline">
                Back to sign in
              </button>
            </p>
          </div>
        )}
      </div>

      <p className="absolute bottom-6 text-sand-400 text-xs">
        Adventure Editor &middot; Share your world
      </p>
    </div>
  );
}
